import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { formatCurrency } from "../lib/utils";
import toast from "react-hot-toast";
import axios from "axios";
import {
  Megaphone,
  Plus,
  Edit,
  Trash2,
  Clock,
  DollarSign,
  MousePointer,
  Image,
  Type,
  Calendar,
  Phone,
  User,
  Link as LinkIcon,
  RefreshCw,
  CheckCircle,
  XCircle,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";

const API = `${window.location.origin}/api`;
const ADMIN_SECRET = "hesabaty-admin-secret-2024-secure";

const AD_PRICES = {
  weekly: 25000,
  monthly: 85000,
  two_months: 150000
};

const DURATION_NAMES = {
  weekly: "أسبوعي (7 أيام)",
  monthly: "شهري (30 يوم)",
  two_months: "شهرين (60 يوم)"
};

export default function AdsAdminPage() {
  const [loading, setLoading] = useState(true);
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState({});
  
  // Dialog states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    ad_type: "text",
    title: "",
    content: "",
    link: "",
    advertiser_name: "",
    advertiser_phone: "",
    duration_type: "weekly"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [adsRes, statsRes] = await Promise.all([
        axios.get(`${API}/ads/all?admin_secret=${ADMIN_SECRET}`),
        axios.get(`${API}/ads/stats?admin_secret=${ADMIN_SECRET}`)
      ]);
      setAds(adsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching ads:", error);
      toast.error("خطأ في جلب البيانات");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      ad_type: "text",
      title: "",
      content: "",
      link: "",
      advertiser_name: "",
      advertiser_phone: "",
      duration_type: "weekly"
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setIsCreateOpen(true);
  };

  const openEditDialog = (ad) => {
    setSelectedAd(ad);
    setFormData({
      ad_type: ad.ad_type,
      title: ad.title,
      content: ad.content,
      link: ad.link || "",
      advertiser_name: ad.advertiser_name,
      advertiser_phone: ad.advertiser_phone,
      duration_type: ad.duration_type
    });
    setIsEditOpen(true);
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.content || !formData.advertiser_name || !formData.advertiser_phone) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    try {
      await axios.post(`${API}/ads/create?admin_secret=${ADMIN_SECRET}`, formData);
      toast.success("تم إنشاء الإعلان بنجاح");
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في إنشاء الإعلان");
    }
  };

  const handleUpdate = async () => {
    if (!selectedAd) return;

    try {
      await axios.put(`${API}/ads/${selectedAd.id}?admin_secret=${ADMIN_SECRET}`, formData);
      toast.success("تم تحديث الإعلان بنجاح");
      setIsEditOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في التحديث");
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الإعلان؟")) return;

    try {
      await axios.delete(`${API}/ads/${adId}?admin_secret=${ADMIN_SECRET}`);
      toast.success("تم حذف الإعلان");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في الحذف");
    }
  };

  const handleToggle = async (adId) => {
    try {
      const res = await axios.put(`${API}/ads/${adId}/toggle?admin_secret=${ADMIN_SECRET}`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في تغيير الحالة");
    }
  };

  const isExpired = (endDate) => {
    return new Date(endDate) < new Date();
  };

  const getRemainingDays = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-amber-600/30 border-t-amber-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="ads-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600">
            <Megaphone className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">
              إدارة الإعلانات
            </h1>
            <p className="text-slate-400">
              إدارة العروض والإعلانات الترويجية
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-slate-600 text-slate-300"
          >
            <RefreshCw className="w-4 h-4 me-2" />
            تحديث
          </Button>
          <Button
            onClick={openCreateDialog}
            className="bg-amber-600 hover:bg-amber-700"
          >
            <Plus className="w-4 h-4 me-2" />
            إعلان جديد
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Megaphone className="w-10 h-10 text-blue-500" />
            <div>
              <p className="text-xs text-slate-400">إجمالي الإعلانات</p>
              <p className="text-2xl font-bold text-white">{stats.total_ads || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="w-10 h-10 text-green-500" />
            <div>
              <p className="text-xs text-slate-400">إعلانات نشطة</p>
              <p className="text-2xl font-bold text-white">{stats.active_ads || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500/20 to-red-600/10 border-red-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-10 h-10 text-red-500" />
            <div>
              <p className="text-xs text-slate-400">إعلانات منتهية</p>
              <p className="text-2xl font-bold text-white">{stats.expired_ads || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <DollarSign className="w-10 h-10 text-amber-500" />
            <div>
              <p className="text-xs text-slate-400">إجمالي الإيرادات</p>
              <p className="text-xl font-bold text-white">{formatCurrency(stats.total_revenue || 0)}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <MousePointer className="w-10 h-10 text-purple-500" />
            <div>
              <p className="text-xs text-slate-400">إجمالي النقرات</p>
              <p className="text-2xl font-bold text-white">{stats.total_clicks || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pricing Info */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-500" />
            أسعار الإعلانات
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-900/50 rounded-lg p-4 text-center border border-slate-700">
              <p className="text-slate-400 text-sm mb-1">أسبوعي</p>
              <p className="text-2xl font-bold text-emerald-400">{formatCurrency(25000)}</p>
              <p className="text-slate-500 text-xs">7 أيام</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4 text-center border border-amber-500/30">
              <p className="text-slate-400 text-sm mb-1">شهري</p>
              <p className="text-2xl font-bold text-amber-400">{formatCurrency(85000)}</p>
              <p className="text-slate-500 text-xs">30 يوم</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4 text-center border border-purple-500/30">
              <p className="text-slate-400 text-sm mb-1">شهرين</p>
              <p className="text-2xl font-bold text-purple-400">{formatCurrency(150000)}</p>
              <p className="text-slate-500 text-xs">60 يوم</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ads Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-amber-500" />
            قائمة الإعلانات
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-amber-500">النوع</TableHead>
                  <TableHead className="text-amber-500">العنوان</TableHead>
                  <TableHead className="text-amber-500">المُعلن</TableHead>
                  <TableHead className="text-amber-500">المدة</TableHead>
                  <TableHead className="text-amber-500">السعر</TableHead>
                  <TableHead className="text-amber-500">النقرات</TableHead>
                  <TableHead className="text-amber-500">الحالة</TableHead>
                  <TableHead className="text-amber-500">الإجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                      لا توجد إعلانات
                    </TableCell>
                  </TableRow>
                ) : (
                  ads.map((ad) => {
                    const expired = isExpired(ad.end_date);
                    const remaining = getRemainingDays(ad.end_date);
                    
                    return (
                      <TableRow key={ad.id} className="border-slate-700 hover:bg-slate-800/50">
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                            ad.ad_type === "banner" ? "bg-purple-500/20 text-purple-400" : "bg-blue-500/20 text-blue-400"
                          }`}>
                            {ad.ad_type === "banner" ? <Image className="w-3 h-3" /> : <Type className="w-3 h-3" />}
                            {ad.ad_type === "banner" ? "بانر" : "نصي"}
                          </span>
                        </TableCell>
                        <TableCell className="text-white font-medium max-w-[200px] truncate">
                          {ad.title}
                        </TableCell>
                        <TableCell className="text-slate-300">
                          <div className="flex flex-col">
                            <span>{ad.advertiser_name}</span>
                            <span className="text-xs text-slate-500" dir="ltr">{ad.advertiser_phone}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-300 text-sm">
                          {expired ? (
                            <span className="text-red-400">منتهي</span>
                          ) : (
                            <span className="text-emerald-400">متبقي {remaining} يوم</span>
                          )}
                        </TableCell>
                        <TableCell className="text-amber-400 font-medium">
                          {formatCurrency(ad.price)}
                        </TableCell>
                        <TableCell className="text-slate-300">
                          {ad.click_count || 0}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                            ad.is_active && !expired ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                          }`}>
                            {ad.is_active && !expired ? (
                              <><CheckCircle className="w-3 h-3" /> نشط</>
                            ) : (
                              <><XCircle className="w-3 h-3" /> {expired ? "منتهي" : "متوقف"}</>
                            )}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggle(ad.id)}
                              className="text-slate-300 hover:text-white h-8"
                            >
                              {ad.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(ad)}
                              className="text-blue-400 hover:text-blue-300 h-8"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(ad.id)}
                              className="text-red-400 hover:text-red-300 h-8"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <Card className="bg-amber-500/10 border-amber-500/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <h3 className="text-amber-400 font-bold mb-1">تنبيه مهم</h3>
              <p className="text-slate-300 text-sm">
                المحتوى الإعلاني من مسؤولية المُعلن (صاحب الشركة أو المحل). نازاريو حساباتي غير مسؤول عن صحة المعلومات أو جودة المنتجات والخدمات المعروضة في الإعلانات.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-500" />
              إنشاء إعلان جديد
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Ad Type */}
            <div className="space-y-2">
              <Label className="text-slate-200">نوع الإعلان</Label>
              <Select value={formData.ad_type} onValueChange={(v) => setFormData({...formData, ad_type: v})}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="text" className="text-white hover:bg-slate-700">
                    <span className="flex items-center gap-2">
                      <Type className="w-4 h-4" /> نصي
                    </span>
                  </SelectItem>
                  <SelectItem value="banner" className="text-white hover:bg-slate-700">
                    <span className="flex items-center gap-2">
                      <Image className="w-4 h-4" /> بانر (صورة)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label className="text-slate-200">مدة الإعلان</Label>
              <Select value={formData.duration_type} onValueChange={(v) => setFormData({...formData, duration_type: v})}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="weekly" className="text-white hover:bg-slate-700">
                    أسبوعي - {formatCurrency(25000)}
                  </SelectItem>
                  <SelectItem value="monthly" className="text-white hover:bg-slate-700">
                    شهري - {formatCurrency(85000)}
                  </SelectItem>
                  <SelectItem value="two_months" className="text-white hover:bg-slate-700">
                    شهرين - {formatCurrency(150000)}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label className="text-slate-200">عنوان الإعلان *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="مثال: عروض نهاية الأسبوع"
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label className="text-slate-200">
                {formData.ad_type === "banner" ? "رابط الصورة *" : "نص الإعلان *"}
              </Label>
              {formData.ad_type === "banner" ? (
                <Input
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="https://example.com/banner.jpg"
                  dir="ltr"
                />
              ) : (
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white min-h-[100px]"
                  placeholder="اكتب نص الإعلان هنا..."
                />
              )}
            </div>

            {/* Link (Optional) */}
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <LinkIcon className="w-4 h-4" />
                رابط خارجي (اختياري)
              </Label>
              <Input
                value={formData.link}
                onChange={(e) => setFormData({...formData, link: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="https://example.com"
                dir="ltr"
              />
            </div>

            {/* Advertiser Name */}
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <User className="w-4 h-4" />
                اسم المُعلن *
              </Label>
              <Input
                value={formData.advertiser_name}
                onChange={(e) => setFormData({...formData, advertiser_name: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="مثال: شركة النور للإلكترونيات"
              />
            </div>

            {/* Advertiser Phone */}
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                هاتف المُعلن *
              </Label>
              <Input
                value={formData.advertiser_phone}
                onChange={(e) => setFormData({...formData, advertiser_phone: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="07701234567"
                dir="ltr"
              />
            </div>

            {/* Price Preview */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-300">السعر:</span>
                <span className="text-2xl font-bold text-amber-400">
                  {formatCurrency(AD_PRICES[formData.duration_type])}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsCreateOpen(false)}
              className="border-slate-600 text-slate-300"
            >
              إلغاء
            </Button>
            <Button
              onClick={handleCreate}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Plus className="w-4 h-4 me-2" />
              إنشاء الإعلان
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-500" />
              تعديل الإعلان
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Title */}
            <div className="space-y-2">
              <Label className="text-slate-200">عنوان الإعلان</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label className="text-slate-200">
                {formData.ad_type === "banner" ? "رابط الصورة" : "نص الإعلان"}
              </Label>
              {formData.ad_type === "banner" ? (
                <Input
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white"
                  dir="ltr"
                />
              ) : (
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white min-h-[100px]"
                />
              )}
            </div>

            {/* Link */}
            <div className="space-y-2">
              <Label className="text-slate-200">رابط خارجي</Label>
              <Input
                value={formData.link}
                onChange={(e) => setFormData({...formData, link: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white"
                dir="ltr"
              />
            </div>

            {/* Advertiser Name */}
            <div className="space-y-2">
              <Label className="text-slate-200">اسم المُعلن</Label>
              <Input
                value={formData.advertiser_name}
                onChange={(e) => setFormData({...formData, advertiser_name: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            {/* Advertiser Phone */}
            <div className="space-y-2">
              <Label className="text-slate-200">هاتف المُعلن</Label>
              <Input
                value={formData.advertiser_phone}
                onChange={(e) => setFormData({...formData, advertiser_phone: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white"
                dir="ltr"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsEditOpen(false)}
              className="border-slate-600 text-slate-300"
            >
              إلغاء
            </Button>
            <Button
              onClick={handleUpdate}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Edit className="w-4 h-4 me-2" />
              حفظ التعديلات
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
